import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Download, Users, Target, Briefcase, GraduationCap, ArrowLeft, Share2, Copy, Check, Trash2, Eye } from "lucide-react";
import ChartSummary from "@/components/ChartSummary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"];

function TeacherMetrics({ user, onLogout }) {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [snapshotDays, setSnapshotDays] = useState(60);
  const [copiedToken, setCopiedToken] = useState("");

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    loadMetrics(selectedClassId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  const loadClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`);
      setClasses(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const loadSnapshots = async () => {
    try {
      const res = await axios.get(`${API}/educator/snapshot/list`);
      setSnapshots(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const openShareModal = async () => {
    setShowShareModal(true);
    await loadSnapshots();
  };

  const createSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      await axios.post(`${API}/educator/snapshot/create`, {
        class_id: selectedClassId || null,
        label: snapshotLabel || null,
        expires_in_days: snapshotDays,
      });
      setSnapshotLabel("");
      await loadSnapshots();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to create share link.");
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const revokeSnapshot = async (token) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revoke this share link? Anyone using it will lose access.")) return;
    try {
      await axios.post(`${API}/educator/snapshot/${token}/revoke`);
      await loadSnapshots();
    } catch (e) { /* ignore */ }
  };

  const copyShareLink = async (token) => {
    const url = `${window.location.origin}/snapshot/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(""), 2000);
    } catch (e) { /* ignore */ }
  };

  const loadMetrics = async (classId) => {
    setLoading(true);
    try {
      const url = classId ? `${API}/educator/metrics?class_id=${classId}` : `${API}/educator/metrics`;
      const res = await axios.get(url);
      setMetrics(res.data);
    } catch (e) {
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const url = selectedClassId
        ? `${API}/educator/metrics/csv?class_id=${selectedClassId}`
        : `${API}/educator/metrics/csv`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cte_report_${selectedClassId || "all_classes"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  const rollups = metrics?.rollups || {};
  const ideaCatData = Object.entries(rollups.idea_categories || {}).map(([name, value]) => ({ name, value }));
  const profileDistData = Object.entries(rollups.profile_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: { D: "Builder", I: "Promoter", S: "Helper", C: "Strategist" }[k] || k,
      value: v,
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8 max-w-6xl overflow-x-hidden" data-testid="teacher-metrics-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-orange-600 font-semibold hover:underline"
            data-testid="tm-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-1">CTE Metrics & Reporting</h1>
          <p className="text-gray-600 text-sm">
            Aggregated Lesson 1 outcomes for Perkins / CLNA reporting. Dual-tagged with{" "}
            <strong>Nevada CTE</strong> + <strong>CCTC</strong>.
          </p>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label htmlFor="tm-class" className="text-sm font-semibold text-gray-700">
              Class:
            </label>
            <select
              id="tm-class"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              className="px-3 py-2 border-2 border-gray-200 rounded-lg bg-white"
              data-testid="tm-class-select"
            >
              <option value="">All my classes</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button
              onClick={openShareModal}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50"
              data-testid="tm-share-snapshot-btn"
              title="Create a token-protected, read-only public link"
            >
              <Share2 className="w-4 h-4" />
              District Snapshot
            </button>
            <button
              onClick={downloadCSV}
              disabled={downloading || !metrics?.student_count}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="tm-download-csv-btn"
              title="Download a Perkins/CLNA-ready CSV"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download CSV (Perkins-ready)"}
            </button>
            </div>
          </div>
        </div>

        {!metrics || metrics.student_count === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-5xl mb-4">📊</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">No student data yet</h2>
            <p className="text-gray-600">
              Once students start completing Middle School Lesson 1, their data will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI icon={<Users className="w-5 h-5" />} label="Students" value={metrics.student_count} color="from-blue-400 to-indigo-500" testId="tm-kpi-students" />
              <KPI icon={<Target className="w-5 h-5" />} label="Lesson 1 Completion" value={`${rollups.completion?.percent || 0}%`} sub={`${rollups.completion?.completed || 0} of ${metrics.student_count} completed`} color="from-green-400 to-emerald-500" testId="tm-kpi-completion" />
              <KPI icon={<Briefcase className="w-5 h-5" />} label="Career Pathways Tagged" value={Object.keys(rollups.career_pathways || {}).length} color="from-orange-400 to-pink-500" testId="tm-kpi-pathways" />
              <KPI icon={<GraduationCap className="w-5 h-5" />} label="Academic Subjects" value={Object.keys(rollups.academic_subjects || {}).length} color="from-purple-400 to-fuchsia-500" testId="tm-kpi-subjects" />
            </div>

            {/* Charts row */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <div
                className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden"
                data-testid="tm-skill-radar"
                role="region"
                aria-labelledby="tm-skill-radar-h"
              >
                <h3 id="tm-skill-radar-h" className="font-bold text-gray-800 mb-3">Top Skill Areas (engagement)</h3>
                <ChartSummary
                  testId="tm-skill-radar-summary"
                  title="Top Skill Areas (engagement)"
                  items={(rollups.skills || []).map(s => ({ label: s.title, value: s.engagement }))}
                />
                {(rollups.skills || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No data yet.</p>
                ) : (
                  <div role="img" aria-label={`Bar chart of top ${(rollups.skills||[]).length} skill areas by engagement`}>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={rollups.skills || []} layout="vertical" margin={{ left: 10, right: 10 }}>
                        <CartesianGrid stroke="#f0f0f0" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="title" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="engagement" fill="#8B5CF6" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div
                className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden"
                data-testid="tm-idea-categories"
                role="region"
                aria-labelledby="tm-idea-categories-h"
              >
                <h3 id="tm-idea-categories-h" className="font-bold text-gray-800 mb-3">Business Idea Categories</h3>
                <ChartSummary
                  testId="tm-idea-categories-summary"
                  title="Business Idea Categories"
                  items={ideaCatData.map(d => ({ label: d.name, value: d.value }))}
                  emptyText="No completed business ideas yet."
                />
                {ideaCatData.length === 0 ? (
                  <p className="text-sm text-gray-500">No completed business ideas yet.</p>
                ) : (
                  <div role="img" aria-label={`Pie chart of ${ideaCatData.length} business idea categories`}>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={ideaCatData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={{ fontSize: 11 }}>
                          {ideaCatData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Profile distribution + pathways */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <div
                className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden"
                data-testid="tm-profile-dist"
                role="region"
                aria-labelledby="tm-profile-dist-h"
              >
                <h3 id="tm-profile-dist-h" className="font-bold text-gray-800 mb-3">Entrepreneur Profile Distribution</h3>
                <ChartSummary
                  testId="tm-profile-dist-summary"
                  title="Entrepreneur Profile Distribution"
                  items={profileDistData.map(d => ({ label: d.name, value: d.value }))}
                  emptyText="No profiles yet."
                />
                {profileDistData.length === 0 ? (
                  <p className="text-sm text-gray-500">No profiles yet.</p>
                ) : (
                  <div role="img" aria-label="Bar chart of entrepreneur profile distribution">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={profileDistData} margin={{ left: 0, right: 10 }}>
                        <CartesianGrid stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#EC4899" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden" data-testid="tm-pathways-list">
                <h3 className="font-bold text-gray-800 mb-3">Career Pathway Distribution</h3>
                {Object.keys(rollups.career_pathways || {}).length === 0 ? (
                  <p className="text-sm text-gray-500">No data yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(rollups.career_pathways || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, count]) => (
                        <li key={name} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{name}</span>
                          <span className="font-bold text-orange-600">{count}</span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Per-student table */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 min-w-0 overflow-hidden" data-testid="tm-student-table">
              <h3 className="font-bold text-gray-800 mb-3">Student Details</h3>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Profile</th>
                      <th className="text-left px-3 py-2">Idea Category</th>
                      <th className="text-left px-3 py-2">Top Skills</th>
                      <th className="text-left px-3 py-2">Subjects</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(metrics.students || []).map((s) => {
                      const status = s.completed
                        ? "✓ Completed"
                        : (s.engagement?.interactions_completed || 0) > 0
                        ? "In Progress"
                        : "Not Started";
                      return (
                        <tr key={s.student.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-900">{s.student.name}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                              s.completed ? "bg-green-100 text-green-700"
                                : (s.engagement?.interactions_completed || 0) > 0
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-500"
                            }`}>{status}</span>
                          </td>
                          <td className="px-3 py-2">
                            {s.profile ? `${s.profile.icon} ${s.profile.name}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{s.idea_category}</td>
                          <td className="px-3 py-2 text-gray-700 text-xs">
                            {(s.top_skills || []).map((sk) => sk.title).join(", ") || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {(s.academic_credit_subjects || []).join(", ") || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* District Snapshot Share Modal */}
        {showShareModal && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowShareModal(false)}
            data-testid="tm-share-modal"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-indigo-600" /> District Snapshot Links
                </h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                  aria-label="Close"
                  data-testid="tm-share-modal-close"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                Create a <strong>token-protected, read-only public link</strong> to share live KPIs with principals,
                district leaders, or grant reviewers — no login required. Links can be revoked anytime.
              </p>

              {/* Create form */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-5">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Label (optional, helps you organize links)
                </label>
                <input
                  type="text"
                  value={snapshotLabel}
                  onChange={(e) => setSnapshotLabel(e.target.value)}
                  placeholder="e.g., Spring 2026 — Period 4"
                  className="w-full px-3 py-2 border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500"
                  data-testid="tm-snapshot-label-input"
                />
                <div className="mt-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Link valid for
                  </label>
                  <div className="flex gap-2 flex-wrap" role="radiogroup" data-testid="tm-snapshot-expiry-picker">
                    {[30, 60, 90].map((d) => {
                      const selected = snapshotDays === d;
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setSnapshotDays(d)}
                          className={`px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all ${
                            selected
                              ? "bg-indigo-600 border-indigo-600 text-white shadow-md"
                              : "bg-white border-indigo-200 text-indigo-700 hover:border-indigo-400"
                          }`}
                          data-testid={`tm-snapshot-days-${d}`}
                          aria-checked={selected}
                          role="radio"
                        >
                          {d} days
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-600">
                    Expires <strong>{new Date(Date.now() + snapshotDays * 86400000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong> · adds credibility to grant submissions and audit trails.
                  </p>
                </div>
                <div className="mt-3 flex justify-between items-center flex-wrap gap-2">
                  <p className="text-xs text-indigo-700">
                    Scope:{" "}
                    <strong>
                      {selectedClassId
                        ? classes.find((c) => c.id === selectedClassId)?.name || "selected class"
                        : "All my classes"}
                    </strong>
                  </p>
                  <button
                    onClick={createSnapshot}
                    disabled={creatingSnapshot}
                    className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50"
                    data-testid="tm-create-snapshot-btn"
                  >
                    {creatingSnapshot ? "Creating..." : "Create Link"}
                  </button>
                </div>
              </div>

              {/* Existing links */}
              <div data-testid="tm-snapshot-list">
                {snapshots.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-6">
                    No share links yet. Create one above.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {snapshots.map((s) => {
                      const url = `${window.location.origin}/snapshot/${s.token}`;
                      const expDt = s.expires_at ? new Date(s.expires_at) : null;
                      const isExpired = expDt && expDt < new Date();
                      const inactive = s.revoked || isExpired;
                      return (
                        <div
                          key={s.token}
                          className={`border-2 rounded-xl p-3 ${
                            inactive ? "bg-gray-50 border-gray-200 opacity-60" : "bg-white border-indigo-200"
                          }`}
                          data-testid={`tm-snapshot-row-${s.token.slice(0, 8)}`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-sm truncate">
                                {s.label || "Untitled snapshot"}
                                {s.revoked && (
                                  <span className="ml-2 text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                    REVOKED
                                  </span>
                                )}
                                {!s.revoked && isExpired && (
                                  <span className="ml-2 text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                    EXPIRED
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5 flex-wrap">
                                <span>Created {new Date(s.created_at).toLocaleDateString()}</span>
                                {expDt && (
                                  <span className={isExpired ? "text-orange-600 font-semibold" : "text-gray-500"}>
                                    {isExpired ? "Expired" : "Valid through"}{" "}
                                    {expDt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {s.view_count || 0} views
                                </span>
                                {s.class_id ? (
                                  <span>Scope: {classes.find((c) => c.id === s.class_id)?.name || "class"}</span>
                                ) : (
                                  <span>Scope: All classes</span>
                                )}
                              </div>
                            </div>
                            {!inactive && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => copyShareLink(s.token)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold rounded-lg"
                                  data-testid={`tm-copy-snap-${s.token.slice(0, 8)}`}
                                >
                                  {copiedToken === s.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  {copiedToken === s.token ? "Copied" : "Copy Link"}
                                </button>
                                <button
                                  onClick={() => revokeSnapshot(s.token)}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-white border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50"
                                  data-testid={`tm-revoke-snap-${s.token.slice(0, 8)}`}
                                  title="Revoke this link"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Revoke
                                </button>
                              </div>
                            )}
                          </div>
                          {!inactive && (
                            <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-xs text-gray-700 font-mono truncate" title={url}>
                              {url}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon, label, value, sub, color, testId }) {
  return (
    <div
      className={`bg-gradient-to-br ${color} rounded-2xl shadow-lg p-5 text-white`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 opacity-90 text-sm mb-1">
        {icon} {label}
      </div>
      <div className="text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs opacity-90 mt-1">{sub}</div>}
    </div>
  );
}

export default TeacherMetrics;
